"use client"

import { useState } from "react"
import Link from "next/link"
import { Check } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/utils/utils"
import { LINKS } from "./links"

type Billing = "mensal" | "anual"

type Plan = {
  name: string
  blurb: string
  monthly: number
  featured?: boolean
  ctaLabel: string
  ctaHref: string
  ctaVariant: "default" | "outline"
  features: string[]
}

const PLANS: Plan[] = [
  {
    name: "Básico",
    blurb: "Para CFCs pequenos começando na plataforma.",
    monthly: 149.9,
    ctaLabel: "Começar grátis",
    ctaHref: LINKS.register,
    ctaVariant: "outline",
    features: ["Até 100 alunos", "Até 3 instrutores", "Agenda, alunos e financeiro", "App do aluno e do instrutor"],
  },
  {
    name: "Profissional",
    blurb: "Para CFCs em crescimento que querem escalar.",
    monthly: 299.9,
    featured: true,
    ctaLabel: "Começar grátis",
    ctaHref: LINKS.register,
    ctaVariant: "default",
    features: ["Até 500 alunos", "Até 15 instrutores", "Contratos por modelo .docx", "Relatórios e mensalidades"],
  },
  {
    name: "Ilimitado",
    blurb: "Sem limites de alunos ou instrutores.",
    monthly: 599.9,
    ctaLabel: "Falar com vendas",
    ctaHref: LINKS.register,
    ctaVariant: "outline",
    features: ["Alunos ilimitados", "Instrutores ilimitados", "Tudo do Profissional", "Suporte prioritário"],
  },
]

const brl = (n: number) => n.toFixed(2).replace(".", ",")
// Plano anual = equivalente a 2 meses grátis (10/12 do mensal).
const perMonth = (monthly: number, billing: Billing) =>
  billing === "anual" ? (monthly * 10) / 12 : monthly

export function Pricing() {
  const [billing, setBilling] = useState<Billing>("anual")

  return (
    <div>
      {/* billing toggle */}
      <div className="mt-6 mb-3.5 flex justify-center">
        <div className="inline-flex gap-1 rounded-4xl border border-border bg-muted/60 p-1.5">
          {(["mensal", "anual"] as const).map((b) => {
            const active = billing === b
            return (
              <button
                key={b}
                onClick={() => setBilling(b)}
                aria-pressed={active}
                className={cn(
                  "inline-flex h-10 items-center gap-2 rounded-4xl px-5 text-sm font-semibold transition-all",
                  active ? "bg-background text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {b === "mensal" ? "Mensal" : "Anual"}
                {b === "anual" && (
                  <span className="rounded-[10px] bg-primary px-1.5 py-0.5 text-[11px] font-bold text-primary-foreground">
                    2 meses grátis
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
      <p className="mb-8 text-center text-[13px] text-muted-foreground">
        {billing === "anual"
          ? "Preços à vista no plano anual — equivalente por mês."
          : "Flexível, cancele quando quiser."}
      </p>

      <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-3">
        {PLANS.map((plan) => (
          <div
            key={plan.name}
            className={cn(
              "relative flex flex-col rounded-2xl bg-card p-7",
              plan.featured
                ? "shadow-[0_20px_50px_-24px_color-mix(in_oklch,var(--primary),transparent_50%)] ring-2 ring-primary"
                : "ring-1 ring-foreground/10"
            )}
          >
            {plan.featured && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-[14px] bg-primary px-3.5 py-1 text-xs font-bold text-primary-foreground">
                Mais popular
              </div>
            )}
            <div className={cn("text-[13px] font-bold tracking-wide uppercase", plan.featured ? "text-primary" : "text-muted-foreground")}>
              {plan.name}
            </div>
            <p className="mt-1.5 mb-4 text-sm text-muted-foreground">{plan.blurb}</p>
            <div className="flex items-baseline gap-1">
              <span className="text-xl font-bold text-muted-foreground">R$</span>
              <span className="font-heading text-[2.75rem] font-extrabold tracking-tight">
                {brl(perMonth(plan.monthly, billing))}
              </span>
              <span className="text-[15px] text-muted-foreground">/mês</span>
            </div>
            <div className="mt-1 mb-5 min-h-[18px] text-[12.5px] text-muted-foreground">
              {billing === "anual"
                ? `cobrado anualmente · economize R$ ${brl(plan.monthly * 2)}`
                : "no plano mensal"}
            </div>
            <Button asChild variant={plan.ctaVariant} className="mb-6 h-11 w-full rounded-4xl">
              <Link href={plan.ctaHref}>{plan.ctaLabel}</Link>
            </Button>
            <ul className="flex flex-col gap-2.5">
              {plan.features.map((f) => (
                <li key={f} className="flex gap-2.5 text-sm">
                  <Check className="size-4.5 flex-none text-primary" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="mt-6 text-center text-[12.5px] text-muted-foreground/70">
        Valores de exemplo — ajuste conforme sua tabela oficial. [SUA TABELA DE PREÇOS]
      </p>
    </div>
  )
}
